import { DASHBOARD_RIGHT_PANEL_WIDTH_PX, OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT } from '@/constants/dimensions';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import AIGeneratedLabel from '@/components/atoms/AIGeneratedLabel';
import { background, text } from '@/theme/colors/palettes';
import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import RecommendationsDisplayV1 from './RecommendationsV1';
import RecommendationsDisplayV2 from './RecommendationsV2';
import InsightsGeneratingNotice from './InsightsGeneratingNotice';
import { TCortexOrganizationInsightsDTO } from '@/types/Cortex.types';
import { TWholenessObservation } from '@/types/Organization.types';
import { buildWholenessObservationText } from '@/utils/wholenessObservation';
import AIOrganizationOverviewModalV2 from './AIOrganizationOverviewModalV2';

interface Props {
  totalScore: number;
  additionalOverviewSection?: React.JSX.Element;
  overviewBottomComponent?: React.JSX.Element;
  cortexInsightsResult?: TCortexOrganizationInsightsDTO;
  // True while the AI is (re)generating — `cortexInsightsResult.insights` may still hold a stale snapshot.
  isInsightsGenerating?: boolean;
  wholenessObservation?: TWholenessObservation;
  organizationName?: string;
}

const OrganizationOverview = ({
  additionalOverviewSection,
  overviewBottomComponent,
  cortexInsightsResult,
  isInsightsGenerating,
  wholenessObservation,
  organizationName,
}: Props) => {
  const insightsVersion = cortexInsightsResult?.insightsVersion ?? 'NONE';
  const hasInsights = insightsVersion === 'V1' || insightsVersion === 'V2';

  // Nothing to show yet and the AI is still working (or hasn't reported back) — show the loader.
  const recommendationsLoading = !hasInsights && cortexInsightsResult?.status !== 'FAILED';
  const recommendationsFailed = !hasInsights && cortexInsightsResult?.status === 'FAILED';

  const canOpenFullReport = hasInsights || !!wholenessObservation;

  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);

  const isInsightsVersionV1 = cortexInsightsResult?.insightsVersion === 'V1';
  const isInsightsVersionV2 = cortexInsightsResult?.insightsVersion === 'V2';

  return (
    <VStack
      width={DASHBOARD_RIGHT_PANEL_WIDTH_PX}
      minHeight={`${OVERVIEW_RIGHT_SIDE_PANEL_HEIGHT}px`}
      borderRadius={'20px'}
      gap={'14px'}
      padding={'20px'}
      backgroundColor={'white'}
      boxShadow={'0px 10px 50px 0px #00417926'}
    >
      <HStack width={'100%'} justifyContent={'space-between'}>
        <Text variant={'loraItalic'} fontStyle={'italic'} fontSize={'16px'} color={text.mediumBlue}>
          Wholeness Observation
        </Text>
        <AIGeneratedLabel />
      </HStack>

      <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'} whiteSpace={'pre-wrap'}>
        {wholenessObservation
          ? buildWholenessObservationText(organizationName, wholenessObservation)
          : 'Wholeness observation is not available yet.'}
      </Text>

      <HStack width={'100%'} justifyContent={'space-between'}>
        <Text variant={'loraItalic'} fontStyle={'italic'} color={text.mediumBlue}>
          MyOmnia Recommendations
        </Text>
        {isInsightsGenerating && !recommendationsLoading && (
          <InsightsGeneratingNotice
            variant="inline"
            estimatedSeconds={cortexInsightsResult?.estimatedTimeSeconds}
            requestId={cortexInsightsResult?.requestId}
          />
        )}
      </HStack>

      {recommendationsLoading ? (
        <InsightsGeneratingNotice
          estimatedSeconds={cortexInsightsResult?.estimatedTimeSeconds}
          requestId={cortexInsightsResult?.requestId}
        />
      ) : recommendationsFailed ? (
        <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'} marginBottom={'20px'}>
          Recommendations couldn&apos;t be generated right now. They&apos;ll refresh automatically once available.
        </Text>
      ) : isInsightsVersionV1 ? (
        <RecommendationsDisplayV1 insights={cortexInsightsResult.insights} />
      ) : isInsightsVersionV2 ? (
        <RecommendationsDisplayV2 insights={cortexInsightsResult.insights} />
      ) : (
        <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'} marginBottom={'20px'}>
          No recommendations available yet.
        </Text>
      )}

      <HStack
        borderRadius={'6px'}
        gap={1.5}
        cursor={canOpenFullReport ? 'pointer' : 'not-allowed'}
        _hover={{ opacity: 0.9 }}
        opacity={canOpenFullReport ? 1 : 0.5}
        onClick={() => (canOpenFullReport ? setIsViewAllModalOpen(true) : undefined)}
        alignItems={'center'}
      >
        <Text variant={'urbanistMaximumBold12'} color={text.mediumBlue}>
          View Full Report
        </Text>
        <SvgIcon
          iconPath={svgIcons.rightArrow}
          color={text.mediumBlue}
          size={10}
          boxHeight={10}
          boxWidth={16}
          style={{ rotate: '90deg' }}
        />
      </HStack>

      {additionalOverviewSection && <VStack style={{ width: '100%' }}>{additionalOverviewSection}</VStack>}

      {overviewBottomComponent && (
        <VStack width={'100%'}>
          <HStack
            width={'111%'}
            height={'1px'}
            marginTop={'4px'}
            marginBottom={'8px'}
            backgroundColor={background.separatorBlue}
          />
          {overviewBottomComponent}
        </VStack>
      )}

      <AIOrganizationOverviewModalV2
        isOpen={isViewAllModalOpen}
        onClose={() => setIsViewAllModalOpen(false)}
        cortexInsightsResult={cortexInsightsResult}
      />
    </VStack>
  );
};

export default OrganizationOverview;
